import { elem, styleInstaller } from "./chrome";

/** Format the browser's last-resort startup message like legacy Abort. */
export function formatFatalError(title: string, error: unknown): string {
  const detail = error instanceof Error
    ? (error.stack || `${error.name}: ${error.message}`)
    : String(error);
  return `${title}\n\n${detail}`;
}

const STYLE_ID = "fatal-error-style";

const STYLE = `
.fatal-error {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  box-sizing: border-box;
  background: #0a0b0d;
  color: #d8dee6;
  font: 13px/1.5 system-ui, sans-serif;
  pointer-events: auto;
}
.fatal-error-title {
  color: #ffb4b4;
  font-size: 1.3rem;
  font-weight: 600;
}
.fatal-error-detail {
  max-width: 900px;
  max-height: 50vh;
  overflow: auto;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 180, 180, 0.24);
  background: rgba(255, 255, 255, 0.04);
  color: #b9c2cc;
  font: 12px/1.45 ui-monospace, Menlo, Consolas, monospace;
  white-space: pre-wrap;
  text-align: left;
}
.fatal-error-reload {
  padding: 6px 18px;
  border-radius: 7px;
  border: 1px solid rgba(255, 236, 170, 0.4);
  background: rgba(255, 236, 170, 0.1);
  color: #f2ecd9;
  font: inherit;
  cursor: pointer;
}
.fatal-error-reload:hover { background: rgba(255, 236, 170, 0.2); }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

/**
 * The screen a player is left with when the client cannot start.
 *
 * `exception.Abort` writes the title, the traceback and the exception through
 * `dbg.TraceError` and then calls `app.Abort()`, which is `PostQuitMessage(0)`
 * -- the window closes and the text is only in `syserr.txt`
 * (`exception.py:26-38`; `PythonApplication.cpp:148-153`). A browser tab
 * cannot close itself and has no `syserr.txt` a player will read, so the same
 * text is put on the screen instead, with the one action the platform does
 * offer in place of a restart.
 */
export class FatalErrorScreen {
  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly detail: HTMLElement;

  constructor(
    container: HTMLElement,
    private readonly onReload: () => void =
    () => container.ownerDocument.defaultView?.location.reload(),
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.root = elem(doc, "div", "fatal-error");
    this.root.hidden = true;
    this.root.setAttribute("role", "alert");
    this.titleEl = elem(doc, "div", "fatal-error-title");
    this.detail = elem(doc, "pre", "fatal-error-detail");
    const reload = doc.createElement("button");
    reload.type = "button";
    reload.className = "fatal-error-reload";
    reload.textContent = "Reload";
    reload.addEventListener("click", () => this.onReload());
    this.root.append(this.titleEl, this.detail, reload);
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** Put `error` on the screen under `title`. There is no way back from here:
   *  the original's own answer to this state is to end the process. */
  show(title: string, error: unknown): void {
    const [head, ...rest] = formatFatalError(title, error).split("\n\n");
    this.titleEl.textContent = head;
    this.detail.textContent = rest.join("\n\n");
    this.root.hidden = false;
  }
}
